import { Button, FormControl, InputLabel } from '@mui/material';
import { SelectChangeEvent } from "@mui/material/Select";
import AddCircleIcon from '@mui/icons-material/AddCircle';

import TextFieldEnter from "~/mui/TextFieldEnter";
import { MySelect, MyMenuItem } from "~/mui/MySelect";
import MyList, { IMyListItem } from "~/mui/MyList";
import { checkIPAddress, checkReferers, getRestrictionValue, compareArray, getId } from "~/utils/utils";
import { useTrans } from '~/hooks/useTrans';
import { MIN_STRING_LENGTH } from "~/const";
import { IClientKey, IRestrictions } from "~/providers/client-interfaces";
import { TRestrictionsTypes } from "~/providers/interfaces";
import { myUseState } from "~/hooks/myUseState";

import "./style.less";

interface IState {
	type: TRestrictionsTypes;
	value: string;
	errorMsg: string;
	itemsType: string;
	items: Array<IMyListItem>;
	placeholder: string;
}

interface IKeyRestrictionsEdit {
	defaultType?: TRestrictionsTypes;
	keyData: IClientKey;
	onUpdate: (restrictions: IRestrictions, isChanged: boolean) => void;
}

export default function KeyRestrictionsEdit({
	defaultType = "ipAddresses",
	keyData = null,
	onUpdate = () => {},
}: IKeyRestrictionsEdit) {
	const { trans, transRestrictionsPlaceholder } = useTrans();
	const { state, setState, updateState } = myUseState<IState>(() => {
		const type = defaultType || "ipAddresses";
		const items = getRestrictionValue(keyData);

		return {
			type,
			value: "",
			errorMsg: "",
			itemsType: type,
			items: items.map(title => ({ id: getId(), title })),
			placeholder: transRestrictionsPlaceholder(type),
		};
	});

	function handleChange(event: SelectChangeEvent) {
		const type = event.target.value as TRestrictionsTypes;

		setState(prev => ({
			...prev,
			type,
			placeholder: transRestrictionsPlaceholder(type),
		}));
	}

	function callOnUpdate(type: string, newItems: Array<IMyListItem>) {
		const origItems = getRestrictionValue(keyData);
		const newItemsStr = newItems.map(item => item.title);
		const newRestrictions = newItems.length
			? {
				[type]: newItemsStr,
			}
			: {};

		onUpdate(newRestrictions, !compareArray(origItems, newItemsStr));
	}

	function addRestriction() {
		const resValue = state.value;
		let errorMsg = "";

		switch (state.type) {
			case "ipAddresses":
				if (!checkIPAddress(resValue)) {
					errorMsg = trans("restrictionsPage.error.ipAddresses");
				}
				break;

			case "referers":
				if (!checkReferers(resValue)) {
					errorMsg = trans("restrictionsPage.error.referers");
				}
				break;

			case "userAgents":
				if (resValue.length < MIN_STRING_LENGTH) {
					errorMsg = trans("restrictionsPage.error.userAgents", { count: MIN_STRING_LENGTH });
				}
				break;

			default:
		}

		if (!errorMsg && state.items.findIndex(item => item.title === resValue) === -1) {
			if (state.type === state.itemsType || state.items.length === 0) {
				const items = [...state.items, {
					id: getId(),
					title: resValue,
				}];

				callOnUpdate(state.type, items);
				updateState({
					errorMsg: "",
					value: "",
					itemsType: state.type,
					items,
				});

				return;
			}

			errorMsg = trans("restrictionsPage.error.moreTypes");
		}

		updateState({
			errorMsg: errorMsg || trans("restrictionsPage.error.duplicate"),
		});
	}

	function removeItemFromList(item: IMyListItem) {
		const items = state.items;
		const ind = state.items.findIndex(stateItem => stateItem.title === item.title);

		if (ind !== -1) {
			items.splice(ind, 1);
			callOnUpdate(state.type, items);
			updateState({ items });
		}
	}

	return <>
		<div className="keyRestrictionsEdit__edit">
			<FormControl className="keyRestrictionsEdit__editColumn1">
				<InputLabel>
					{ trans("type") }
				</InputLabel>
				<MySelect
					value={state.type}
					onChange={handleChange}
					label={ trans("type") }
					size="small"
				>
					<MyMenuItem value="ipAddresses">
						{ trans("restrictionsPage.restriction.ipAddresses") }
					</MyMenuItem>
					<MyMenuItem value="referers">
						{ trans("restrictionsPage.restriction.referers") }
					</MyMenuItem>
					<MyMenuItem value="userAgents">
						{ trans("restrictionsPage.restriction.userAgents") }
					</MyMenuItem>
				</MySelect>
			</FormControl>
			<FormControl className="keyRestrictionsEdit__editColumn2">
				<TextFieldEnter
					label={trans("value")}
					value={state.value}
					onChange={event => updateState({ value: event.target.value })}
					onEnter={addRestriction}
					color="success"
					size="small"
					placeholder={ state.placeholder }
					{ ...state.errorMsg
						? {
							error: true,
							helperText: state.errorMsg,
						}
						: {}
					}
				/>
			</FormControl>
		</div>
		<div className="keyRestrictionsEdit__addBtnArea">
			<Button color="success" variant="contained" startIcon={<AddCircleIcon />} onClick={addRestriction}>
				{ trans("add") }
			</Button>
		</div>
		{ state.items.length > 0 && <div>
			<h2 className="restrictionsEditModal__title">
				{ trans("restrictionsPage.active") }:
			</h2>
			<MyList items={state.items} onRemove={removeItemFromList} />
		</div> }
	</>;
}
